import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Printer, AlertTriangle, Phone, Heart, Activity, ClipboardCheck,
  Copy, Check, StopCircle, Users, Ambulance, MessageSquare, Wind, Moon,
} from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';

const P = SECTIONS.sops;
const AMBER = '#D97706';
const RED = '#DC2626';

const PATIENT_SCRIPTS = [
  {
    id: 'onset',
    label: 'At onset of episode',
    text: "You're safe. I'm stopping the procedure now. I'd like you to lie flat and we'll lift your legs — this usually passes quickly.",
  },
  {
    id: 'reassure',
    label: 'Reassurance during recovery',
    text: "This is a common reaction — a vasovagal response. You're safe. Just stay still and breathe slowly.",
  },
  {
    id: 'anxiety',
    label: 'Anxiety / panic reaction',
    text: "You're safe. Let's breathe together — slowly in through your nose for four counts, out through your mouth for six. Nothing else needs to happen right now.",
  },
  {
    id: 'discharge',
    label: 'Before discharge',
    text: "Before you stand up, I'd like to check you're steady. Please sip water. Avoid standing quickly for the next couple of hours. If it happens again, contact your GP or attend an Emergency Department.",
  },
];

const RIS_NOTE = 'Patient experienced a vasovagal episode during procedure. Managed conservatively with supine positioning and observation. Full recovery achieved prior to discharge.';

const SIGNS = ['Lightheadedness', 'Pallor', 'Sweating', 'Nausea', 'Bradycardia', 'Hypotension', 'Loss of consciousness'];
const RED_FLAGS = ['Chest pain', 'Shortness of breath', 'Seizure activity', 'Persistent unconsciousness', 'Severe hypotension', 'Neurological deficit'];

export default function ClinicalEpisodesPage() {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(null);

  const copy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* noop */
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .min-h-screen { background: white !important; }
          .print-container { padding: 0 !important; max-width: none !important; }
          h1, h2, h3 { color: #0E2D5C !important; }
          .print-compact { gap: 0.5rem !important; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 print-container" data-testid="clinical-episodes-page">
        <div className="no-print flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/sops')}
            data-testid="ce-back"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to SOPs
          </button>
          <button
            onClick={() => window.print()}
            data-testid="ce-print"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg text-white"
            style={{ backgroundColor: AMBER }}
          >
            <Printer className="w-3.5 h-3.5" /> Print laminated quick-ref
          </button>
        </div>

        {/* Cover */}
        <div className="rounded-2xl border p-6 sm:p-8 mb-6" style={{ borderColor: `${AMBER}33`, background: `linear-gradient(135deg, ${AMBER}0F, transparent 60%)` }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${AMBER}15`, color: AMBER }}>
              <Heart className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${AMBER}1A`, color: AMBER }}>
              SOP · v1.0 · DIAS Std 2
            </span>
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Patient Clinical Episodes — Quick Reference</h1>
          <p className="text-sm text-muted-foreground mt-1">Vasovagal syncope · pre-syncope · anxiety · hypoglycaemia · acute deterioration</p>
          <div className="mt-5 flex flex-wrap gap-5 text-xs">
            <Meta label="Owner" value="Medical Director" />
            <Meta label="Governance" value="Practice Manager / Clinical Lead" />
            <Meta label="Applies in" value="CT · US · Waiting area · Procedure rooms" />
          </div>
        </div>

        {/* Core rule */}
        <div className="rounded-2xl border p-5 mb-6" style={{ borderColor: `${AMBER}55`, backgroundColor: `${AMBER}0A` }} data-testid="ce-core-principle">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: AMBER }} />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: AMBER }}>Core Principle</p>
              <p className="text-base font-semibold text-foreground mt-1">
                At the first sign of patient deterioration, the procedure <span className="underline decoration-2" style={{ textDecorationColor: AMBER }}>stops</span> and patient safety takes priority over workflow.
              </p>
            </div>
          </div>
        </div>

        {/* Recognition — two columns: common signs + red flags */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <SignsCard icon={Activity} tone={AMBER} label="Common Signs" items={SIGNS} />
          <SignsCard icon={AlertTriangle} tone={RED} label="Red Flags — escalate immediately" items={RED_FLAGS} />
        </div>

        {/* 5-step flow */}
        <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold" style={{ backgroundColor: `${AMBER}1A`, color: AMBER }}>1</span>
          Immediate Response — 5 Steps (all staff)
        </h2>
        <div className="space-y-2 mb-8 print-compact" data-testid="ce-flow">
          <FlowStep n="1" icon={StopCircle} tone={RED} title="STOP" body="Stop the procedure immediately. Remove the needle if safe to do so." highlight />
          <FlowStep n="2" icon={Users} tone={AMBER} title="CALL FOR HELP" body="Alert nearby staff. Escalate to the radiologist." />
          <FlowStep n="3" icon={Moon} tone={AMBER} title="POSITION THE PATIENT" body={<>Suspected vasovagal: <strong>lay flat, elevate legs</strong>. If unconscious: open airway, recovery position if required.<br /><span className="text-red-600 font-semibold">Never sit the patient upright early</span> — this is one of the most common clinical errors.</>} />
          <FlowStep n="4" icon={Activity} tone={AMBER} title="ASSESS" body="Consciousness · Airway · Breathing · Pulse." />
          <FlowStep n="5" icon={Heart} tone={AMBER} title="MANAGE" body={
            <ul className="mt-1 space-y-1">
              <li><strong>Vasovagal:</strong> reassure · maintain supine · monitor until recovery.</li>
              <li><strong>Hypoglycaemia (if conscious):</strong> give oral glucose. Escalate if not improving.</li>
              <li><strong>Anxiety / panic:</strong> calm reassurance · slow-breathing guidance.</li>
            </ul>
          } />
        </div>

        {/* Escalation */}
        <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold" style={{ backgroundColor: `${RED}15`, color: RED }}>2</span>
          Escalation Pathway
        </h2>
        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          <EscalationCard tone="#16A34A" label="Recovers quickly" body="Continue observation. Do NOT discharge immediately. Assess fitness first." />
          <EscalationCard tone={RED} icon={Phone} label="Not improving / red flags" body="Call 000 (ambulance) without delay." />
          <EscalationCard tone={RED} icon={Ambulance} label="Severe deterioration" body="Initiate emergency response. Follow the anaphylaxis / emergency SOP if relevant." />
        </div>

        {/* Monitoring + pre-discharge */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Monitoring</p>
            <ul className="space-y-2 text-sm text-foreground">
              <li className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: AMBER }} />Observe until stable.</li>
              <li className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: AMBER }} />Record vital signs where possible.</li>
              <li className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: AMBER }} /><strong>Minimum observation: 15–30 min post-recovery.</strong></li>
            </ul>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><ClipboardCheck className="w-3.5 h-3.5" /> Before discharge — patient must be</p>
            <ul className="space-y-2 text-sm text-foreground">
              <li className="flex items-start gap-2"><span className="w-4 h-4 rounded border border-border mt-0.5 shrink-0" />Fully conscious</li>
              <li className="flex items-start gap-2"><span className="w-4 h-4 rounded border border-border mt-0.5 shrink-0" />Haemodynamically stable</li>
              <li className="flex items-start gap-2"><span className="w-4 h-4 rounded border border-border mt-0.5 shrink-0" />Able to mobilise safely</li>
            </ul>
            <p className="text-[11px] text-muted-foreground mt-3">Advise: hydrate · avoid sudden standing · GP follow-up if needed.</p>
          </div>
        </div>

        {/* Patient scripts */}
        <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold" style={{ backgroundColor: `${AMBER}1A`, color: AMBER }}>3</span>
          What to Say to Patients <span className="text-xs font-normal text-muted-foreground">(calm, plain-language scripts)</span>
        </h2>
        <div className="space-y-3 mb-8" data-testid="ce-scripts">
          {PATIENT_SCRIPTS.map(s => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5" style={{ color: AMBER }} />
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{s.label}</p>
                </div>
                <button
                  onClick={() => copy(s.id, s.text)}
                  data-testid={`ce-copy-${s.id}`}
                  className="no-print inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-md border transition-colors"
                  style={copiedId === s.id
                    ? { borderColor: '#16A34A', color: '#16A34A', backgroundColor: '#16A34A10' }
                    : { borderColor: `${AMBER}40`, color: AMBER }}
                >
                  {copiedId === s.id ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <p className="text-sm text-foreground leading-relaxed italic">"{s.text}"</p>
            </div>
          ))}
        </div>

        {/* RIS example note + incident reporting */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="rounded-xl border border-border bg-card p-5" data-testid="ce-ris-note">
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">RIS example note</p>
              <button
                onClick={() => copy('ris', RIS_NOTE)}
                data-testid="ce-copy-ris"
                className="no-print inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-md border"
                style={copiedId === 'ris'
                  ? { borderColor: '#16A34A', color: '#16A34A', backgroundColor: '#16A34A10' }
                  : { borderColor: `${AMBER}40`, color: AMBER }}
              >
                {copiedId === 'ris' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
            </div>
            <p className="text-sm text-foreground leading-relaxed font-mono bg-muted/60 rounded-md p-3">{RIS_NOTE}</p>
            <p className="text-[11px] text-muted-foreground mt-2">Mandatory RIS fields: time · symptoms · interventions · recovery time · staff involved · escalation.</p>
          </div>
          <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-red-600 mb-3 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Formal incident report required if</p>
            <ul className="space-y-2 text-sm text-foreground">
              {['Prolonged', 'Severe', 'Recurrent', 'Unusual'].map(t => (
                <li key={t} className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 bg-red-500" />{t} episode</li>
              ))}
            </ul>
            <p className="text-[11px] text-muted-foreground mt-3">Standard uncomplicated vasovagal episodes → RIS note only. Anything atypical → Incident Report.</p>
          </div>
        </div>

        {/* Breathing + responsibility */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><Wind className="w-3.5 h-3.5" /> Slow-breathing guidance (anxiety)</p>
            <ol className="space-y-1.5 text-sm text-foreground list-decimal pl-5">
              <li>Sit or lie them down — do not force upright.</li>
              <li>"Breathe in through your nose, slowly — count of 4."</li>
              <li>"Hold gently — count of 2."</li>
              <li>"Breathe out through your mouth — count of 6."</li>
              <li>Repeat until pulse settles. Monitor silently between cycles.</li>
            </ol>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Responsibility</p>
            <table className="w-full text-sm">
              <tbody>
                {[
                  ['Radiologist', 'Clinical oversight, escalation decision'],
                  ['Radiographer / Sono', 'Immediate response, positioning'],
                  ['Reception', 'Call for assistance / ambulance'],
                  ['Practice Manager', 'Audit and review'],
                ].map(([r, d]) => (
                  <tr key={r} className="border-b border-border/60 last:border-0">
                    <td className="py-1.5 pr-3 font-semibold text-foreground whitespace-nowrap">{r}</td>
                    <td className="py-1.5 text-foreground/80">{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="no-print text-center text-[11px] text-muted-foreground mb-2">
          Owner: Medical Director · Clinical Lead: Practice Manager · Review: Annual
        </p>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <span className="uppercase tracking-wider text-[10px] font-bold text-muted-foreground">{label}</span>
      <div className="text-foreground font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function SignsCard({ icon: Icon, tone, label, items }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tone}15`, color: tone }}>
          <Icon className="w-4 h-4" />
        </div>
        <p className="font-['Outfit'] text-sm font-bold" style={{ color: tone }}>{label}</p>
      </div>
      <ul className="grid grid-cols-1 gap-1.5 text-sm text-foreground">
        {items.map((t, i) => (
          <li key={i} className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: tone }} /><span>{t}</span></li>
        ))}
      </ul>
    </div>
  );
}

function FlowStep({ n, icon: Icon, tone, title, body, highlight = false }) {
  return (
    <div
      className="flex gap-3 rounded-xl border p-4"
      style={highlight
        ? { borderColor: tone, backgroundColor: `${tone}0A` }
        : { borderColor: 'hsl(var(--border))' }}
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold" style={{ backgroundColor: `${tone}15`, color: tone }}>{n}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color: tone }} /> {title}
        </p>
        <div className="text-xs text-foreground/85 mt-1 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function EscalationCard({ tone, icon: Icon, label, body }) {
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: `${tone}40`, backgroundColor: `${tone}0A` }}>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon ? <Icon className="w-4 h-4" style={{ color: tone }} /> : <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tone }} />}
        <p className="text-xs font-bold uppercase tracking-wider" style={{ color: tone }}>{label}</p>
      </div>
      <p className="text-sm text-foreground/85 leading-relaxed">{body}</p>
    </div>
  );
}
